import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  Pagination,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Person as PersonIcon,
  Mail as MailIcon,
  Assignment as ContractIcon,
} from '@mui/icons-material';
import { StatusChip } from '../../../common/components';

const ITEMS_PER_PAGE = 10;

const getStatusColor = (summary = {}) => {
  if ((summary.failed_count ?? 0) === 0) return 'success';
  if ((summary.successful_count ?? 0) === 0) return 'error';
  return 'warning';
};

const getStatusIcon = (summary = {}) => {
  if ((summary.failed_count ?? 0) === 0) return <SuccessIcon color="success" />;
  if ((summary.successful_count ?? 0) === 0) return <ErrorIcon color="error" />;
  return <InfoIcon color="warning" />;
};

const groupByContract = (details = []) =>
  details.reduce((acc, detail) => {
    const contractNumber = detail.contractId || 'Sin contrato';
    if (!acc[contractNumber]) {
      acc[contractNumber] = [];
    }
    acc[contractNumber].push(detail);
    return acc;
  }, {});

const paginateEntries = (entries, page) => {
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  return Object.entries(entries).slice(startIndex, endIndex);
};

const DebtEmailResultsDialog = ({
  open,
  onClose,
  results,
  title = 'Resultado del envío de emails',
  successSectionLabel = 'Correos enviados',
  errorSectionLabel = 'Correos con error',
}) => {
  const [successPage, setSuccessPage] = useState(1);
  const [failedPage, setFailedPage] = useState(1);

  const summary = results?.summary || {
    total_emails: 0,
    successful_count: 0,
    failed_count: 0,
  };

  const successfulGrouped = useMemo(
    () => groupByContract(results?.successfulDetails || []),
    [results]
  );

  const failedGrouped = useMemo(() => groupByContract(results?.failedDetails || []), [results]);

  const successfulPaginated = paginateEntries(successfulGrouped, successPage);
  const failedPaginated = paginateEntries(failedGrouped, failedPage);

  const successfulTotalPages = Math.max(
    1,
    Math.ceil(Object.keys(successfulGrouped).length / ITEMS_PER_PAGE)
  );
  const failedTotalPages = Math.max(
    1,
    Math.ceil(Object.keys(failedGrouped).length / ITEMS_PER_PAGE)
  );

  useEffect(() => {
    setSuccessPage(1);
    setFailedPage(1);
  }, [results]);

  const renderContractDetails = (contractsData, type = 'success') => {
    if (!contractsData || contractsData.length === 0) return null;

    return (
      <List dense>
        {contractsData.map(([contractNumber, details]) => (
          <ListItem
            key={`${type}-${contractNumber}`}
            alignItems="flex-start"
            sx={{
              mb: 1,
              borderRadius: 1,
              border: 1,
              borderColor: type === 'success' ? 'success.light' : 'error.light',
              backgroundColor: type === 'success' ? 'success.50' : 'error.50',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                mb: 1,
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <ContractIcon color={type === 'success' ? 'success' : 'error'} fontSize="small" />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Contrato: {contractNumber}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {details.length} email{details.length > 1 ? 's' : ''}
                  </Typography>
                </Box>
              </Box>
              <StatusChip
                label={type === 'success' ? 'Enviado' : 'Error'}
                color={type === 'success' ? 'success' : 'error'}
                variant="outlined"
              />
            </Box>

            {details.map((detail, index) => (
              <Box
                key={`${contractNumber}-${index}`}
                sx={{
                  width: '100%',
                  p: 1.5,
                  mb: index !== details.length - 1 ? 1 : 0,
                  borderRadius: 1,
                  backgroundColor: 'background.paper',
                  boxShadow: 0.5,
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon fontSize="small" color="primary" />
                  <Typography variant="body2" fontWeight={500}>
                    {detail.subscriber || 'Suscriptor desconocido'}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                  <MailIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    {detail.email || 'Sin email'}
                  </Typography>
                </Box>

                {detail.error && (
                  <Box display="flex" alignItems="flex-start" gap={1} mt={1}>
                    <ErrorIcon fontSize="small" color="error" />
                    <Typography variant="caption" color="error">
                      {detail.error}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
          </ListItem>
        ))}
      </List>
    );
  };

  if (!results) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '420px' } }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          {getStatusIcon(summary)}
          <Typography variant="h6">{title}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity={getStatusColor(summary)} sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={500}>
            {results.message}
          </Typography>
        </Alert>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="text.primary">
                  {summary.total_emails ?? 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total de correos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="success.main">
                  {summary.successful_count ?? 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Envíos exitosos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="error.main">
                  {summary.failed_count ?? 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Envíos fallidos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box>
          {Object.keys(successfulGrouped).length > 0 && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <SuccessIcon color="success" />
                  <Typography variant="subtitle1" fontWeight={500}>
                    {successSectionLabel} ({Object.keys(successfulGrouped).length} contratos)
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {renderContractDetails(successfulPaginated, 'success')}
                {successfulTotalPages > 1 && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                      count={successfulTotalPages}
                      page={successPage}
                      onChange={(event, value) => setSuccessPage(value)}
                      color="success"
                      size="small"
                    />
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          {Object.keys(failedGrouped).length > 0 && (
            <Accordion defaultExpanded={Object.keys(successfulGrouped).length === 0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <ErrorIcon color="error" />
                  <Typography variant="subtitle1" fontWeight={500}>
                    {errorSectionLabel} ({Object.keys(failedGrouped).length} contratos)
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {renderContractDetails(failedPaginated, 'error')}
                {failedTotalPages > 1 && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                      count={failedTotalPages}
                      page={failedPage}
                      onChange={(event, value) => setFailedPage(value)}
                      color="error"
                      size="small"
                    />
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          )}
        </Box>

        {Object.keys(successfulGrouped).length === 0 && Object.keys(failedGrouped).length === 0 && (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              No hay detalles disponibles para mostrar.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DebtEmailResultsDialog;
